import {FC, useCallback, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';

import {removeUndefined} from '@shared/lib/type_utils';
import {phonetic, sanitize} from '@shared/phonetic';

import {UnthemedButton, UnthemedNavLink} from '@shared-web/components/core/button';
import {Input} from '@shared-web/components/core/input';
import {SvgIcon} from '@shared-web/components/core/svg_icon';
import {boyIcon} from '@shared-web/components/icons/boy_icon';
import {crossIcon} from '@shared-web/components/icons/cross_icon';
import {girlIcon} from '@shared-web/components/icons/girl_icon';

import {useDb} from '@src/lib/db_store';
import {Palette} from '@src/theme';

interface SearchProps {}

enum SearchMatchType {
  TEXT_EXACT = 'text-exact',
  TEXT_STARTS_WITH = 'text-starts-with',
  TEXT_SUBSTRING = 'text-substring',
  PHONETIC_EXACT = 'phonetic-exact',
  PHONETIC_STARTS_WITH = 'phonetic-starts-with',
  PHONETIC_SUBSTRING = 'phonetic-substring',
}

interface SearchMatch {
  matchType: SearchMatchType;
  matchScore: number;
}

const matchTypeByMode = {
  text: {
    exact: SearchMatchType.TEXT_EXACT,
    startsWith: SearchMatchType.TEXT_STARTS_WITH,
    substring: SearchMatchType.TEXT_SUBSTRING,
  },
  phonetic: {
    exact: SearchMatchType.PHONETIC_EXACT,
    startsWith: SearchMatchType.PHONETIC_STARTS_WITH,
    substring: SearchMatchType.PHONETIC_SUBSTRING,
  },
};
function computeMatches(opts: {
  query: string;
  name: string;
  mode: 'text' | 'phonetic';
}): SearchMatch[] {
  const {query, name, mode} = opts;
  const matchTypes = matchTypeByMode[mode];
  const scores: SearchMatch[] = [];
  if (query === name) {
    scores.push({
      matchType: matchTypes.exact,
      matchScore: 1,
    });
  }
  if (query.startsWith(name)) {
    scores.push({
      matchType: matchTypes.startsWith,
      matchScore: query.length / name.length,
    });
  }
  const queryIndex = name.indexOf(query);
  if (queryIndex !== -1) {
    scores.push({
      matchType: matchTypes.substring,
      matchScore: 1 / (queryIndex + 1),
    });
  }

  return scores;
}

interface NameSearchMatch {
  match: SearchMatch;
  name: string;
  count: number;
}

const searchMatchTypeScore: Record<SearchMatchType, number> = {
  [SearchMatchType.TEXT_EXACT]: 10,
  [SearchMatchType.PHONETIC_EXACT]: 9,
  [SearchMatchType.TEXT_STARTS_WITH]: 8,
  [SearchMatchType.PHONETIC_STARTS_WITH]: 7,
  [SearchMatchType.TEXT_SUBSTRING]: 6,
  [SearchMatchType.PHONETIC_SUBSTRING]: 5,
};
function compareSearchMatches(a: NameSearchMatch, b: NameSearchMatch): number {
  if (a.match.matchType !== b.match.matchType) {
    return searchMatchTypeScore[b.match.matchType] - searchMatchTypeScore[a.match.matchType];
  }
  if (a.match.matchScore !== b.match.matchScore) {
    return b.match.matchScore - a.match.matchScore;
  }
  return b.count - a.count;
}

export const Search: FC<SearchProps> = () => {
  const [searchText, setSearchText] = useState('');
  const db = useDb(['boy', 'girl']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [resultsVisible, setResultsVisible] = useState(false);

  const sortedNames = useMemo(() => {
    if (!db) {
      return undefined;
    }
    const query = sanitize(searchText).toUpperCase();
    const queryPhonetic = phonetic(query);
    // Get the best match for each name
    const namesAndMatches = removeUndefined(
      db.map(dbName => {
        const {phonetic, totalByName} = dbName;

        // Store all the matches we've found on that name
        const allMatches: NameSearchMatch[] = [];

        // Find the most popular spelling of the name
        const first = totalByName[0];
        if (first !== undefined) {
          // Find all the phonetic matches for the query
          const phoneticMatches = computeMatches({
            query: queryPhonetic,
            name: phonetic,
            mode: 'phonetic',
          });
          const {name, count} = first;
          allMatches.push(...phoneticMatches.map(match => ({match, name, count})));

          // Find the matches on all the different spellings
          for (const {name, count} of totalByName) {
            const nameMatches = computeMatches({query, name, mode: 'text'});
            allMatches.push(...nameMatches.map(match => ({match, name, count})));
          }
        }

        const bestMatch = allMatches.sort(compareSearchMatches)[0];
        if (bestMatch === undefined) {
          return undefined;
        }
        return {dbName, bestMatch};
      })
    );

    const sortedNames = namesAndMatches.sort((a, b) =>
      compareSearchMatches(
        {...a.bestMatch, count: a.dbName.totalRecent},
        {...b.bestMatch, count: b.dbName.totalRecent}
      )
    );
    return sortedNames.slice(0, 100);
  }, [db, searchText]);

  const handleCloseClick = useCallback(() => {
    setSearchText('');
    inputRef.current?.blur();
    setResultsVisible(false);
  }, [setSearchText]);

  const handleInputFocus = useCallback(() => {
    setResultsVisible(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => setResultsVisible(false), 100);
  }, []);

  return (
    <Wrapper>
      <InputWrapper>
        <Input
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={inputRef}
          value={searchText}
          syncState={setSearchText}
          width="100%"
          placeholder="Rechercher un prénom"
        />
        {resultsVisible ? (
          <CloseButton onClick={handleCloseClick}>
            <SvgIcon icon={crossIcon} size="1.5rem" color={Palette.primaryColor} />
          </CloseButton>
        ) : null}
      </InputWrapper>
      {resultsVisible
        ? sortedNames?.map(name => (
            <SearchLine to={`/browse/${name.dbName.key}`} key={name.dbName.key}>
              {name.dbName.sex === 'boy' ? (
                <SearchLineSex icon={boyIcon} color="#7eb7c0" width="1.5rem" height="1.28rem" />
              ) : (
                <SearchLineSex icon={girlIcon} color="#c487b9" width="1.5rem" height="1.5rem" />
              )}
              <SearchLineName>{name.bestMatch.name}</SearchLineName>
              <SearchLineRank>{`#${name.dbName.rankRecent}`}</SearchLineRank>
            </SearchLine>
          ))
        : null}
    </Wrapper>
  );
};

Search.displayName = 'Search';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SearchLine = styled(UnthemedNavLink)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #ffffff88;
  border-radius: 0.5rem;
`;

const SearchLineName = styled.div`
  flex-grow: 1;
  font-weight: 600;
  color: #00000099;
`;

const SearchLineSex = styled(SvgIcon)`
  flex-shrink: 0;
`;

const SearchLineRank = styled.div`
  flex-shrink: 0;
  color: #00000054;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const CloseButton = styled(UnthemedButton)`
  position: absolute;
  height: 100%;
  right: 0;
  top: 0;
  aspect-ratio: 1 / 1;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
`;
